import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTasks, FaCheckCircle, FaCoins } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

function DailyQuests() {
  const { user, updateUser } = useAuth();
  const [quests, setQuests]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Le check-in crée les lignes de quêtes du jour côté serveur (idempotent).
    // On l'attend avant de charger les quêtes pour éviter de lire une liste vide
    // si ce composant se monte avant que DailyStreak ait fini son check-in.
    api.post('/streak/checkin')
      .catch(() => {})
      .then(() => api.get('/streak/quests'))
      .then(({ data }) => setQuests(data.quests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const claim = async (questType) => {
    try {
      const { data } = await api.post(`/streak/quests/${questType}/claim`);
      toast.success(`+${data.coinsEarned} pièces !`);
      if (user) updateUser({ ...user, coins: data.newCoins });
      setQuests((prev) =>
        prev.map((q) => q.quest_type === questType ? { ...q, claimed: true } : q)
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  if (loading) return null;
  if (!quests.length) return null;

  const allDone = quests.every((q) => q.claimed);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-5 mb-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <FaTasks size={13} className="text-qfl-gold" />
        <span
          className="text-xs font-bold uppercase tracking-widest text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          Quêtes du jour
        </span>
        {allDone && (
          <span className="text-xs text-green-400 font-bold ml-auto">
            Tout complété ! ✓
          </span>
        )}
      </div>

      <div
        className="rounded-2xl overflow-hidden"
        style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        {quests.map((q, i) => (
          <div
            key={q.quest_type}
            className="flex items-center gap-3 px-4 py-3"
            style={{
              borderBottom: i < quests.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              opacity: q.claimed ? 0.5 : 1,
            }}
          >
            {/* Icône état */}
            {q.claimed ? (
              <FaCheckCircle size={16} className="text-green-400 flex-shrink-0" />
            ) : q.completed ? (
              <div className="w-4 h-4 rounded-full bg-qfl-gold flex-shrink-0 animate-pulse" />
            ) : (
              <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ border: '2px solid rgba(255,255,255,0.25)' }}
              />
            )}

            {/* Label + barre de progression */}
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-semibold text-white leading-tight"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                {q.label}
              </p>
              <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: q.completed ? '#22c55e' : 'linear-gradient(90deg,#f0b429,#fbbf24)' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (q.progress / q.target) * 100)}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </div>
              <p className="text-xs text-white/40 mt-0.5">
                {q.progress}/{q.target}
              </p>
            </div>

            {/* Récompense + bouton claim */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="flex items-center gap-1 text-xs font-bold text-qfl-gold">
                <FaCoins size={10} />
                {q.reward_coins}
              </span>
              {q.completed && !q.claimed && (
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => claim(q.quest_type)}
                  className="text-xs font-bold px-2 py-0.5 rounded-lg"
                  style={{ background: 'linear-gradient(135deg,#f0b429,#d4920a)', color: '#0d2b1a' }}
                >
                  Réclamer
                </motion.button>
              )}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default DailyQuests;
